import React, { useRef, useState } from 'react';

/**
 * The read-only questions a Conv-AI guidance answer offers, as one-click asks.
 *
 * A suggestion is nothing but a question the dispatcher could have typed. A click
 * hands its exact text to `onAsk` — the chat's normal send handler — so it goes
 * through the same POST /query, thread, auth, routing and safety checks as a typed
 * question. Nothing is executed here, nothing is paged locally ("Show more" is just
 * the words "Show more"), and there is no confirmation UI: the backend only offers
 * read-only questions, and anything else it would have to ask about anyway.
 *
 * `onAsk` is passed only to the latest answer while no request is in flight. Without
 * it the questions still show (the answer stays readable) but are inert, so earlier
 * answers never accumulate live buttons.
 */
export interface ChatSuggestionsProps {
  /** The backend's own heading for these questions, e.g. "You can ask". */
  title?: string;
  /** The questions, exactly as the backend sent them. */
  questions: string[];
  /** Absent when this answer is no longer the one being replied to. */
  onAsk?: ((question: string) => void) | undefined;
}

const ChatSuggestions: React.FC<ChatSuggestionsProps> = ({ title, questions, onAsk }) => {
  // One answer asks once. The ref closes the gap before the parent re-renders with
  // loading=true, so a double-click (or a click on a second chip) cannot send twice.
  const askedRef = useRef(false);
  const [asked, setAsked] = useState(false);
  const enabled = Boolean(onAsk) && !asked;

  const ask = (question: string) => {
    if (!onAsk || askedRef.current) return;
    askedRef.current = true;
    setAsked(true);
    onAsk(question);
  };

  return (
    <div className="w-full min-w-0" role="group" aria-label={title || undefined}>
      {title && <p className="mb-2 text-sm font-semibold text-text">{title}</p>}
      <div className="flex flex-wrap gap-2">
        {questions.map((question, index) => (
          <button
            key={`${index}-${question}`}
            type="button"
            data-testid="chat-suggestion"
            disabled={!enabled}
            onClick={() => ask(question)}
            className="max-w-full rounded-full border border-border bg-surface px-3 py-1 text-left text-sm text-text transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:text-text-muted disabled:hover:border-border disabled:hover:text-text-muted"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ChatSuggestions;
